import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Errors } from "../../constants/errors";
import {
  INSTAGRAM_MENSSEGER_GET_LOADING,
  INSTAGRAM_MENSSEGER_SYNC_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { InstagramAccount } from "./interface/api/get-instagram-accounts";
import { instagramMenssegerApiService } from "./services/api";
import { useIntl } from "react-intl";
import {
  getInstagramMenssegerAccountsFailureAction,
  getInstagramMenssegerAccountsInitAction,
  getInstagramMenssegerAccountsSuccessAction,
  getInstagramMenssegerFailureAction,
  getInstagramMenssegerInitAction,
  getInstagramMenssegerSuccessAction,
  postBindInstagramMenssegerFailureAction,
  postBindInstagramMenssegerInitAction,
  postBindInstagramMenssegerSuccessAction,
  postUnbindInstagramMenssegerFailureAction,
  postUnbindInstagramMenssegerInitAction,
  postUnbindInstagramMenssegerSuccessAction,
} from "./store/api-actions";
import {
  accountsSelector,
  errorCodeSelector,
  errorMessageSelector,
  settingSelector,
} from "./store/selector";

export function useInstagramMenssegerCore() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const settings = useSelector(settingSelector);
  const accounts = useSelector(accountsSelector);
  const errorCode = useSelector(errorCodeSelector);
  const errorMessage = useSelector(errorMessageSelector);
  const { turnOnLoading, turnOffLoading } = useGlobalCore();

  const unbind = useCallback(
    async (companyId: string, onSuccess?: () => void) => {
      try {
        dispatch(postUnbindInstagramMenssegerInitAction());
        await instagramMenssegerApiService.unbind(companyId);
        dispatch(postUnbindInstagramMenssegerSuccessAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(postUnbindInstagramMenssegerFailureAction({ error }));
      }
    },
    [dispatch]
  );

  const bind = useCallback(
    async (
      companyId: string,
      account: InstagramAccount,
      userToken: string,
      onSuccess?: () => void,
      options?: { silent?: boolean }
    ) => {
      const silent = options?.silent ?? false;
      try {
        dispatch(postBindInstagramMenssegerInitAction());
        if (!silent) {
          turnOnLoading(INSTAGRAM_MENSSEGER_SYNC_LOADING);
        }
        const settings = await instagramMenssegerApiService.bind(companyId, {
          oauthToken: account.token,
          userToken,
          accountId: account.id,
        });
        dispatch(postBindInstagramMenssegerSuccessAction({ settings }));
        if (!silent) {
          turnOffLoading(INSTAGRAM_MENSSEGER_SYNC_LOADING, 500);
          toast.success(intl.formatMessage({ id: "app.toast.sucess12" }));
        }
        onSuccess?.();
      } catch (error: any) {
        const messengerErrorCodes = [
          Errors.MessageDissabled,
          Errors.MessageNotAllowed,
          Errors.NotBusinessAccount,
        ];
        if (error.request?.status === 403) {
          const code: number = JSON.parse(error.request.response).code || 0;
          const message: string =
            JSON.parse(error.request.response).message || "";
          if (messengerErrorCodes.includes(code)) {
            if (silent) {
              console.warn("Instagram Messenger bind skipped:", code, message);
            } else {
              dispatch(
                postBindInstagramMenssegerFailureAction({
                  errorCode: code,
                  errorMessage: message,
                })
              );
            }
          } else if (!silent) {
            toast.error(intl.formatMessage({ id: "app.toast.error30" }));
            console.error(error, code);
          } else {
            console.warn("Instagram Messenger bind failed:", error, code);
          }
        } else if (!silent) {
          toast.error(intl.formatMessage({ id: "app.toast.error30" }));
          console.error(error);
        } else {
          console.warn("Instagram Messenger bind failed:", error);
        }
        if (!silent) {
          turnOffLoading(INSTAGRAM_MENSSEGER_SYNC_LOADING);
        }
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const bindForPageToken = useCallback(
    async (companyId: string, pageToken: string, userToken: string) => {
      try {
        const igAccounts = await instagramMenssegerApiService.accounts(
          companyId,
          userToken
        );
        const igAccount = igAccounts.find((account) => account.token === pageToken);
        if (!igAccount) return;
        await bind(companyId, igAccount, userToken, undefined, { silent: true });
      } catch (error) {
        console.warn("Failed to bind Instagram Messenger for page:", error);
      }
    },
    [bind]
  );

  const get = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getInstagramMenssegerInitAction());
        turnOnLoading(INSTAGRAM_MENSSEGER_GET_LOADING);
        const settings = await instagramMenssegerApiService.get(companyId);
        dispatch(getInstagramMenssegerSuccessAction({ settings }));
        turnOffLoading(INSTAGRAM_MENSSEGER_GET_LOADING, 1500);
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_MENSSEGER_GET_LOADING);
        console.error(error);
        dispatch(getInstagramMenssegerFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading]
  );

  const getAccounts = useCallback(
    async (companyId: string, token: string) => {
      try {
        dispatch(getInstagramMenssegerAccountsInitAction());
        turnOnLoading(INSTAGRAM_MENSSEGER_SYNC_LOADING);
        const accounts = await instagramMenssegerApiService.accounts(companyId, token);
        dispatch(getInstagramMenssegerAccountsSuccessAction({ accounts }));
        turnOffLoading(INSTAGRAM_MENSSEGER_SYNC_LOADING, 500);
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_MENSSEGER_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error31" }));
        console.error(error);
        dispatch(getInstagramMenssegerAccountsFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  return {
    settings,
    errorCode,
    errorMessage,
    unbind,
    bind,
    bindForPageToken,
    get,
    getAccounts,
    accounts,
  };
}
